/**
 * Recipient-side thread surfacing (group-threading P4, Option B).
 *
 * A group thread is created + owned by ONE user; when they add a second
 * participant, the server mirrors the transcript into the shared
 * `group_thread_messages` store (P3b) and firestore.rules admit that thread's
 * participants (P3c). The recipient's OWN thread index, however, holds no record
 * of a thread they never created — so nothing surfaces it into their sidebar,
 * they can never open it, the overlay read never runs, and they see nothing (and
 * with no active thread, no "+ add people" affordance either).
 *
 * This module closes that gap PURELY: given the group threads the signed-in user
 * participates in (from the server-mediated membership reader, which returns ONLY
 * authenticated-caller memberships whose `participantIds` include that caller),
 * it selects the ones not already local and shapes a
 * lightweight ChatThread STUB for each. The caller persists the stub through the
 * SAME index write guest-adoption uses (`upsertChatThread`), so the thread
 * becomes a first-class LOCAL thread and the existing open/read pipeline
 * (`getThreadMessages` → group overlay merge) fills in the shared transcript.
 *
 * Why a stub is enough: P3b writes BOTH the human turn (with `senderId`) and the
 * assistant turn to the shared store, so the overlay is a COMPLETE transcript for
 * any participant — the recipient needs no message store of their own, and their
 * future replies round-trip through the shared store the same way.
 *
 * Fail-closed + additive: the caller gates every call on the account-scoped group
 * READ flag (testing-only), so this flow is inert where shared reads are off.
 * Pure (no Firebase / React / clock) so it unit-tests in isolation.
 */
import type { ChatThread } from '../../components/GlobalChatSystem/types';
import { titleIsUserOwned } from '../threadMetadata/titleHistory';
import { normalizeCanonicalThreadTitle } from './canonicalThreadTitle';

/**
 * Minimal membership row this module consumes — structurally satisfied by the
 * reader's `MembershipReadThread` and the panel source's
 * `MembershipThreadSnapshotLike` (both expose exactly these fields).
 */
export interface SurfaceableMembershipRow {
  threadId: string;
  workspaceId?: string;
  participantIds: string[];
  createdBy?: string;
  createdAt?: number;
  /** Canonical owner title from server membership metadata. */
  title?: string;
}

/** Generic label when no participant display names resolve. */
export const SHARED_THREAD_TITLE_FALLBACK = 'Group thread';

export interface SelectThreadsToSurfaceArgs {
  rows: readonly SurfaceableMembershipRow[] | null | undefined;
  /** Ids already present locally (owned threads + already-materialized stubs). */
  localThreadIds: ReadonlySet<string>;
  /** Resolved account/workspace — required hard guard from the caller context. */
  accountId: string | null | undefined;
  /** Signed-in user; a row that does not name them is never surfaced (defensive). */
  selfId: string | null | undefined;
}

/**
 * Pick the membership rows to surface. Fail-closed on every axis:
 *   • no self identity / no account ⇒ surface nothing (the flag gate lives in the
 *     caller; these guards keep the fn safe to call unconditionally in tests);
 *   • a row must name a `threadId` and include `selfId` in `participantIds`;
 *   • a thread already present locally is skipped (owner viewing their own thread,
 *     or an already-materialized stub) — no duplicate chip, no write churn.
 * De-duplicated by `threadId`, first occurrence wins.
 */
export function selectThreadsToSurface(
  args: SelectThreadsToSurfaceArgs,
): SurfaceableMembershipRow[] {
  const selfId = (args.selfId ?? '').trim();
  const accountId = (args.accountId ?? '').trim();
  if (!selfId || !accountId) return [];

  const rows = Array.isArray(args.rows) ? args.rows : [];
  const localIds = args.localThreadIds ?? new Set<string>();
  const out: SurfaceableMembershipRow[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    const threadId = (row?.threadId ?? '').trim();
    if (!threadId || seen.has(threadId)) continue;
    if (localIds.has(threadId)) continue;

    const participantIds = Array.isArray(row?.participantIds)
      ? row.participantIds.map((id: unknown) => String(id ?? '').trim()).filter(Boolean)
      : [];
    if (!participantIds.includes(selfId)) continue;
    // 2026-07-31 multi-device directive (self-only skip): a row whose
    // participants resolve to ONLY the caller needs no surfacing — the caller
    // OWNS that thread and its real index doc already lives in their own
    // store (create-event memberships seed participants=[actor], so these
    // rows are abundant). Stubbing one merely merge-clobbered the real doc
    // (title → 'Group thread': no OTHER participant resolves a label;
    // messageCount → 1) on every reload and tab focus.
    if (!participantIds.some((id: string) => id !== selfId)) continue;

    const workspaceId = (row?.workspaceId ?? '').trim();
    const createdBy = (row?.createdBy ?? '').trim() || undefined;

    seen.add(threadId);
    out.push({
      threadId,
      participantIds,
      workspaceId: workspaceId || undefined,
      createdBy,
      createdAt: Number.isFinite(row?.createdAt as number) ? Number(row!.createdAt) : undefined,
      title: normalizeCanonicalThreadTitle(
        row?.title,
        [threadId, workspaceId, createdBy, ...participantIds],
      ),
    });
  }
  return out;
}

export interface SharedThreadLabelArgs {
  row: SurfaceableMembershipRow;
  selfId: string;
  /** uid → display name (from the account roster). Missing ids are omitted. */
  memberLabels?: Record<string, string>;
}

/**
 * A human label for the surfaced thread: the OTHER participants' display names,
 * joined in participant order (stable across refreshes). Falls back to the
 * generic title when nothing resolves.
 */
export function deriveSharedThreadLabel(args: SharedThreadLabelArgs): string {
  const selfId = (args.selfId ?? '').trim();
  const labels = args.memberLabels ?? {};
  const others = (Array.isArray(args.row?.participantIds) ? args.row.participantIds : [])
    .map((id: unknown) => String(id ?? '').trim())
    .filter((id: string) => id && id !== selfId)
    .map((id: string) => normalizeCanonicalThreadTitle(labels[id], [id]))
    .filter((label): label is string => Boolean(label));
  if (others.length === 0) return SHARED_THREAD_TITLE_FALLBACK;
  return others.join(', ');
}

export interface RelabelCandidate {
  threadId: string;
  title: string;
}

export interface SelectStubsToRelabelArgs {
  rows: readonly SurfaceableMembershipRow[] | null | undefined;
  /** The user's local thread records (id + current title is all that's read). */
  localThreads: readonly { id: string; title?: string; userRenamed?: boolean }[];
  accountId: string | null | undefined;
  selfId: string | null | undefined;
  /** uid → display name (from the account roster). */
  memberLabels?: Record<string, string>;
}

/**
 * Pick already-surfaced stubs whose title is still DEGRADED — a raw id or
 * generic fallback minted when the stub was created before the roster labels
 * resolved (or, for stubs minted before the members-doc rules fix, when the
 * roster read was denied outright). Verified live 2026-07-23: both QA sides
 * sat in the SAME thread while the member's chip was titled with the owner's
 * 28-char uid, which read as a DIFFERENT thread and sent the QA pair hunting
 * across identically-named chips.
 *
 * A stub qualifies only when BOTH hold:
 *   • its current title is an id, the generic fallback, or empty — a
 *     custom or first-message title is never touched;
 *   • the label derived WITH `memberLabels` differs — i.e. a real name has
 *     actually resolved, unless an id must immediately become the neutral label.
 * Same fail-closed guards as surfacing: no self/account ⇒ nothing; a row must
 * name the self. De-duplicated by threadId.
 */
export function selectStubsToRelabel(args: SelectStubsToRelabelArgs): RelabelCandidate[] {
  const selfId = (args.selfId ?? '').trim();
  const accountId = (args.accountId ?? '').trim();
  if (!selfId || !accountId) return [];

  const rows = Array.isArray(args.rows) ? args.rows : [];
  const locals = Array.isArray(args.localThreads) ? args.localThreads : [];
  const localById = new Map<string, { title: string; userRenamed: boolean }>();
  for (const t of locals) {
    const id = (t?.id ?? '').trim();
    if (id && !localById.has(id)) {
      localById.set(id, { title: (t?.title ?? '').trim(), userRenamed: t?.userRenamed === true });
    }
  }

  const out: RelabelCandidate[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const threadId = (row?.threadId ?? '').trim();
    if (!threadId || seen.has(threadId)) continue;
    if (!localById.has(threadId)) continue; // not local — surfacing's job, not relabel's

    const participantIds = Array.isArray(row?.participantIds)
      ? row.participantIds.map((id: unknown) => String(id ?? '').trim()).filter(Boolean)
      : [];
    if (!participantIds.includes(selfId)) continue;
    const workspaceId = (row?.workspaceId ?? '').trim() || undefined;
    const createdBy = (row?.createdBy ?? '').trim() || undefined;
    const canonicalTitle = normalizeCanonicalThreadTitle(
      row?.title,
      [threadId, workspaceId, createdBy, ...participantIds],
    );
    const normalizedRow: SurfaceableMembershipRow = {
      threadId,
      participantIds,
      workspaceId,
      createdBy,
      title: canonicalTitle,
    };
    const local = localById.get(threadId)!;
    const currentTitle = local.title;
    // ONE spelling of "a person named this" (2026-09-18, titleIsUserOwned):
    // a name the viewer chose outranks the creator's canonical title on their
    // own list, the same rule the server's shouldWriteParticipantIndexTitle
    // enforces on the write side.
    if (titleIsUserOwned(local)) continue;
    if (canonicalTitle) {
      if (canonicalTitle !== currentTitle) {
        seen.add(threadId);
        out.push({ threadId, title: canonicalTitle });
      }
      continue;
    }
    const degraded = deriveSharedThreadLabel({ row: normalizedRow, selfId, memberLabels: {} });
    const currentIsIdLike = Boolean(currentTitle) && !normalizeCanonicalThreadTitle(
      currentTitle,
      [threadId, workspaceId, createdBy, ...participantIds],
    );
    const isDegraded =
      currentTitle === '' ||
      currentTitle === SHARED_THREAD_TITLE_FALLBACK ||
      currentTitle === degraded ||
      currentIsIdLike;
    if (!isDegraded) continue;

    const better = deriveSharedThreadLabel({ row: normalizedRow, selfId, memberLabels: args.memberLabels });
    if (!better || better === currentTitle || (better === degraded && !currentIsIdLike)) continue;

    seen.add(threadId);
    out.push({ threadId, title: better });
  }
  return out;
}

export interface BuildSharedThreadStubArgs {
  row: SurfaceableMembershipRow;
  label: string;
  /**
   * Injected ISO activity stamp — the fn stays clock-free / deterministic in
   * tests and simply echoes what it is given. 2026-07-31 multi-device
   * directive: production callers MUST pass the membership-derived stamp
   * (`surfacedThreadActivityIso`, i.e. the row's createdAt, epoch fallback),
   * never wall-clock now — a shared 'now' stamped identical fresh recency
   * onto every surfaced stub on every device, reload and focus, clobbering
   * real ordering.
   */
  nowIso: string;
}

/**
 * Shape a ChatThread STUB for a surfaced group thread. Stamped as REAL content
 * (`hasUserContent:true`, `messageCount:1`) so NO lifecycle path treats it as an
 * empty draft to reuse or garbage-collect — the real transcript lives in the
 * shared store and arrives via the overlay on open. `lastActiveAt`/`lastViewedAt`
 * echo the injected activity stamp (see BuildSharedThreadStubArgs.nowIso —
 * membership createdAt in production, so a genuinely-new shared thread still
 * surfaces near the top while an old one keeps its honest place); `createdAt`
 * prefers the membership row's createdAt so the record is honest about origin.
 */
export function buildSharedThreadStub(args: BuildSharedThreadStubArgs): ChatThread {
  const createdAtIso = Number.isFinite(args.row?.createdAt as number)
    ? new Date(Number(args.row.createdAt)).toISOString()
    : args.nowIso;
  const knownIds = [
    args.row?.threadId,
    args.row?.workspaceId,
    args.row?.createdBy,
    ...(Array.isArray(args.row?.participantIds) ? args.row.participantIds : []),
  ];
  const title = normalizeCanonicalThreadTitle(args.row?.title, knownIds)
    || normalizeCanonicalThreadTitle(args.label, knownIds)
    || SHARED_THREAD_TITLE_FALLBACK;
  const workspaceId = (args.row?.workspaceId ?? '').trim();
  return {
    id: args.row.threadId,
    title,
    createdAt: createdAtIso,
    lastActiveAt: args.nowIso,
    lastViewedAt: args.nowIso,
    unreadCount: 0,
    isProcessing: false,
    messageCount: 1,
    hasUserContent: true,
    // ORG BINDING (2026-08-21 shared-thread labeling): carry the membership
    // row's OWN workspace so the stub is created bound to ITS org — the
    // create path's binding stamp prefers this explicit value over whichever
    // workspace happens to be active in the viewer's window (which
    // permanently mis-filed foreign-org group threads). A row without a
    // workspace stays unbound, exactly as before.
    ...(workspaceId ? { accountId: workspaceId } : {}),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Surfacing idempotency (2026-07-31 multi-device directive)
//
// The caller's old loop stubbed every membership row whose threadId was not in
// the CURRENT top-40 working set, on mount AND on every tab focus — so real
// index docs living outside the window were merge-clobbered (title →
// 'Group thread', messageCount → 1, recency → a shared 'now') forever, and
// rules-rejected writes for archived threads retried on every focus. The
// helpers below make the flow idempotent:
//   • a persistent per-(instance, uid) surfaced-marker so reloads/focus
//     re-runs skip already-processed rows without any server read;
//   • a server existence probe before any write — an existing doc is NEVER
//     rewritten (it simply lives outside the window until activity returns
//     it);
//   • membership-createdAt activity stamps for genuinely-new stubs;
//   • session memory for rules-rejected writes (no focus-storm retries);
//   • an injected archive-tombstone probe replicating writeThreadToServer's
//     guard (the stub path bypasses the hook's write choke point).
// Dependencies are injected so the module stays free of Firebase/React/clock
// imports and unit-tests in isolation.
// ─────────────────────────────────────────────────────────────────────────────

/** Minimal Storage surface the marker helpers need (localStorage-compatible). */
export interface SurfacedMarkerStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Per-(instance, uid) marker key — a new uid or surface never inherits another's marker. */
export function surfacedThreadMarkerKey(instanceId: string, uid: string): string {
  return `prest0_shared_surfaced__${instanceId || 'global_chat'}__u_${uid || 'anon'}`;
}

/** Load the persisted set of already-surfaced thread ids (empty on any miss). */
export function loadSurfacedThreadIds(
  storage: SurfacedMarkerStorageLike | null | undefined,
  instanceId: string,
  uid: string,
): Set<string> {
  if (!storage) return new Set();
  try {
    const raw = storage.getItem(surfacedThreadMarkerKey(instanceId, uid));
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string' && id.length > 0));
  } catch {
    return new Set();
  }
}

/** Union `threadIds` into the persisted marker (best-effort, additive only). */
export function markThreadsSurfaced(
  storage: SurfacedMarkerStorageLike | null | undefined,
  instanceId: string,
  uid: string,
  threadIds: Iterable<string>,
): void {
  if (!storage) return;
  try {
    const existing = loadSurfacedThreadIds(storage, instanceId, uid);
    let changed = false;
    for (const threadId of threadIds) {
      if (!threadId || existing.has(threadId)) continue;
      existing.add(threadId);
      changed = true;
    }
    if (!changed) return;
    storage.setItem(surfacedThreadMarkerKey(instanceId, uid), JSON.stringify([...existing]));
  } catch {
    /* best-effort — a storage miss only costs an extra existence probe later */
  }
}

/**
 * The activity stamp a genuinely-new stub carries: the membership row's
 * createdAt (honest recency — a thread shared a month ago surfaces a month
 * deep, a thread shared just now surfaces at the top), falling back to the
 * EPOCH — never wall-clock now, which would fake fresh activity on every
 * device that ever surfaces the row.
 */
export function surfacedThreadActivityIso(row: SurfaceableMembershipRow): string {
  return Number.isFinite(row?.createdAt as number)
    ? new Date(Number(row.createdAt)).toISOString()
    : new Date(0).toISOString();
}

/** Firestore rules rejections are terminal for the session — never retried. */
function isPermissionRejection(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code.toLowerCase().includes('permission-denied');
}

export interface MaterializeSurfacedThreadsArgs {
  /** Rows already selected by `selectThreadsToSurface`. */
  rows: readonly SurfaceableMembershipRow[];
  selfId: string;
  instanceId: string;
  /** uid → display name (from the account roster), for the stub label. */
  memberLabels?: Record<string, string>;
  /** Persistent surfaced-marker store (localStorage in prod); null/undefined disables persistence. */
  storage?: SurfacedMarkerStorageLike | null;
  /**
   * SESSION memory of rows whose stub writes were rules-rejected (mutated in
   * place). Deliberately NOT persisted: a later rules/data fix should get a
   * fresh chance on the next session.
   */
  rejectedThreadIds: Set<string>;
  /** Durable archive-tombstone probe (writeThreadToServer's guard, replicated). */
  isThreadTombstoned?: (threadId: string) => boolean;
  /** Server existence probe for the thread's index doc. */
  threadIndexDocExists: (threadId: string) => Promise<boolean>;
  /** The genuinely-new-stub create write (callers pass isCreate:true downstream). */
  createThreadStub: (stub: ChatThread) => Promise<void>;
}

export interface MaterializeSurfacedThreadsResult {
  /** Stubs genuinely created this run (marker-persisted). */
  created: string[];
  /** Rows whose index doc already exists — nothing written (marker-persisted). */
  skippedExisting: string[];
  /** Rows whose create was rules-rejected this run (session-remembered). */
  rejected: string[];
}

/**
 * Materialize the selected membership rows as local threads, idempotently.
 * Per row: session-rejection and tombstone guards → server existence probe
 * (existing doc ⇒ record only, NO write) → stub create stamped from the
 * membership's createdAt. Probe failures fail CLOSED for the write (never
 * risk clobbering a real doc) and stay un-marked so the next focus retries.
 */
export async function materializeSurfacedThreads(
  args: MaterializeSurfacedThreadsArgs,
): Promise<MaterializeSurfacedThreadsResult> {
  const created: string[] = [];
  const skippedExisting: string[] = [];
  const rejected: string[] = [];
  for (const row of args.rows) {
    const threadId = (row?.threadId ?? '').trim();
    if (!threadId) continue;
    if (args.rejectedThreadIds.has(threadId)) continue;
    if (args.isThreadTombstoned?.(threadId)) continue;
    let exists = false;
    try {
      exists = await args.threadIndexDocExists(threadId);
    } catch {
      continue; // probe miss → fail closed, retry next focus/mount
    }
    if (exists) {
      skippedExisting.push(threadId);
      continue;
    }
    const label = deriveSharedThreadLabel({
      row,
      selfId: args.selfId,
      memberLabels: args.memberLabels,
    });
    const stub = buildSharedThreadStub({ row, label, nowIso: surfacedThreadActivityIso(row) });
    try {
      await args.createThreadStub(stub);
      created.push(threadId);
    } catch (error) {
      if (isPermissionRejection(error)) {
        args.rejectedThreadIds.add(threadId);
        rejected.push(threadId);
      }
      // Transient failures stay un-marked and un-remembered → next focus retries.
    }
  }
  markThreadsSurfaced(args.storage, args.instanceId, args.selfId, [...created, ...skippedExisting]);
  return { created, skippedExisting, rejected };
}
